import { EditableContent } from '../../edit/EditableContent';
import { LucideIcon } from '../../icons/LucideIcon';
import { resolveSectionLucideIcon } from '../../icons/ctaLucideMap';
import { pickBlockField } from '../../lib/localized';
import type { BlockRenderProps } from '../../types';

function safeHttpsUrl(raw: string): string | undefined {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  try {
    const url = new URL(trimmed);
    return url.protocol === 'https:' ? url.toString() : undefined;
  } catch {
    return undefined;
  }
}

export function LiveGpsLocationEmbed({
  block,
  locale,
  isActive,
  activeEditTarget,
  editable,
}: BlockRenderProps) {
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const sectionIconKey = pickBlockField(block.content, 'sectionIcon', locale);
  const statusLabel = pickBlockField(block.content, 'statusLabel', locale);
  const address = pickBlockField(block.content, 'address', locale);
  const updatedAt = pickBlockField(block.content, 'updatedAt', locale);
  const mapEmbedUrl = safeHttpsUrl(pickBlockField(block.content, 'mapEmbedUrl', locale));
  const target = isActive ? activeEditTarget : null;
  const sectionIcon = resolveSectionLucideIcon(sectionIconKey, 'location');

  return (
    <section
      className={['ws-live-gps', isActive ? 'ws-block--active' : ''].filter(Boolean).join(' ')}
      data-block-id={block.blockId}
      data-component={block.componentType}
      id="gps"
    >
      <div className="ws-live-gps__inner">
        <div className="ws-live-gps__copy">
          <div className="ws-live-gps__header">
            <EditableContent
              as="span"
              fieldKey="sectionIcon"
              editable={editable}
              activeEditTarget={target}
              className="ws-live-gps__icon-hit"
            >
              <LucideIcon icon={sectionIcon} size="md" className="ws-live-gps__icon" />
            </EditableContent>
            <EditableContent
              as="h2"
              fieldKey="sectionTitle"
              editable={editable}
              activeEditTarget={target}
              className="ws-live-gps__title"
            >
              {sectionTitle}
            </EditableContent>
          </div>
          {statusLabel || editable ? (
            <EditableContent
              fieldKey="statusLabel"
              editable={editable}
              activeEditTarget={target}
              className="ws-live-gps__status"
            >
              {statusLabel}
            </EditableContent>
          ) : null}
          <EditableContent
            fieldKey="address"
            editable={editable}
            activeEditTarget={target}
            className="ws-live-gps__address"
          >
            {address}
          </EditableContent>
          {updatedAt || editable ? (
            <EditableContent
              fieldKey="updatedAt"
              editable={editable}
              activeEditTarget={target}
              className="ws-live-gps__updated"
            >
              {updatedAt}
            </EditableContent>
          ) : null}
        </div>
        <div className="ws-live-gps__visual">
          {mapEmbedUrl ? (
            <iframe
              className="ws-live-gps__embed"
              src={mapEmbedUrl}
              title={sectionTitle || 'Live location'}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          ) : (
            <div className="ws-live-gps__placeholder" aria-hidden />
          )}
        </div>
      </div>
    </section>
  );
}
